'use client'

import { ClientSelector } from '@/components/client/ClientSelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import {
    SignDocument,
    addSignDocumentSigner,
    getSignDocument,
    removeSignDocumentSigner,
    sendSignDocumentForSigning,
} from '@/lib/api/administrative/sign-api'
import { Client } from '@/lib/api/clients/client-api'
import { Partner } from '@/lib/api/common/interfaces/partner.interface'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { SocketContext } from '@/lib/providers/socket-provider'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useContext, useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdCheckCircle,
    MdDelete,
    MdDownload,
    MdHourglassEmpty,
    MdPerson,
    MdPersonAdd,
    MdSend,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
    PENDING: { label: 'Pendente', className: 'badge-ghost' },
    SENT: { label: 'Enviado', className: 'badge-info' },
    SIGNING: { label: 'Assinando', className: 'badge-warning' },
    COMPLETED: { label: 'Concluído', className: 'badge-success' },
    CANCELLED: { label: 'Cancelado', className: 'badge-error' },
}

const SIGNER_TYPE_LABELS: Record<string, string> = {
    PARTNER: 'Parceiro',
    CLIENT: 'Cliente',
    OTHER: 'Outro',
}

const ORIGIN_LABELS: Record<string, string> = {
    CONTRACT: 'Contrato',
}

function getOriginLabel(origin?: string | null) {
    if (!origin) return 'Avulso'
    return ORIGIN_LABELS[origin] || origin
}

type SignerType = 'PARTNER' | 'CLIENT' | 'OTHER'

export default function SignDocumentDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { socket, isConnected } = useContext(SocketContext)
    const { canUpdate } = useResourcePermissions('administrative', 'sign')

    const [document, setDocument] = useState<SignDocument | null>(null)
    const [loading, setLoading] = useState(true)

    // Estado do formulário de signatários
    const [signerType, setSignerType] = useState<SignerType>('OTHER')
    const [signerName, setSignerName] = useState('')
    const [signerEmail, setSignerEmail] = useState('')
    const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null)
    const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null)
    const [selectedClientId, setSelectedClientId] = useState<number | null>(null)
    const [selectedClient, setSelectedClient] = useState<Client | null>(null)
    const [adding, setAdding] = useState(false)
    const [sending, setSending] = useState(false)

    useEffect(() => {
        if (id) {
            loadDocument()
        }
    }, [id])

    // Ouvir atualizações em tempo real via socket
    useEffect(() => {
        if (!socket || !isConnected || !user?.companyId) return

        const eventName = `sign-document-updated-${user.companyId}`

        const handleUpdate = (updatedDoc: SignDocument) => {
            if (updatedDoc.id === Number(id)) {
                setDocument(updatedDoc)
            }
        }

        socket.on(eventName, handleUpdate)

        return () => {
            socket.off(eventName, handleUpdate)
        }
    }, [socket, isConnected, user?.companyId, id])

    async function loadDocument() {
        try {
            setLoading(true)
            const response = await getSignDocument(Number(id))
            if (response.success) {
                setDocument(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar documento')
                navigate('/commerce/subscriptions')
            }
        } catch {
            toast.error('Erro ao carregar documento')
            navigate('/commerce/subscriptions')
        } finally {
            setLoading(false)
        }
    }

    function resetSignerForm() {
        setSignerName('')
        setSignerEmail('')
        setSelectedPartnerId(null)
        setSelectedPartner(null)
        setSelectedClientId(null)
        setSelectedClient(null)
    }

    async function saveSignerToBackend(data: {
        name: string
        email: string
        partnerId?: number
        clientId?: number
        type: 'PARTNER' | 'CLIENT' | 'OTHER'
    }) {
        if (!document) return
        setAdding(true)
        try {
            const response = await addSignDocumentSigner(document.id, data)
            if (response.success) {
                await loadDocument()
                resetSignerForm()
            } else {
                toast.error(response.error || 'Erro ao adicionar signatário')
            }
        } catch {
            toast.error('Erro ao adicionar signatário')
        } finally {
            setAdding(false)
        }
    }

    async function addSigner() {
        if (!signerName.trim()) {
            toast.error('Preencha o nome do signatário')
            return
        }
        if (!signerEmail.trim()) {
            toast.error('Preencha o e-mail do signatário')
            return
        }
        await saveSignerToBackend({
            name: signerName,
            email: signerEmail,
            type: 'OTHER',
        })
    }

    async function addMyself() {
        if (!user?.name || !user?.email) {
            toast.error('Seu usuário não possui nome ou e-mail cadastrado')
            return
        }
        const alreadyAdded = document?.signers?.some(
            s => s.email.toLowerCase() === user.email.toLowerCase(),
        )
        if (alreadyAdded) {
            toast.error('Você já está na lista de signatários')
            return
        }
        await saveSignerToBackend({
            name: user.name,
            email: user.email,
            type: 'OTHER',
        })
    }

    async function removeSigner(signerId: number) {
        if (!document) return
        try {
            const response = await removeSignDocumentSigner(document.id, signerId)
            if (response.success) {
                await loadDocument()
            } else {
                toast.error(response.error || 'Erro ao remover signatário')
            }
        } catch {
            toast.error('Erro ao remover signatário')
        }
    }

    async function handleSendForSigning() {
        if (!document) return
        if (!document.signers || document.signers.length === 0) {
            toast.error('Adicione ao menos um signatário')
            return
        }

        try {
            setSending(true)
            const response = await sendSignDocumentForSigning(document.id)
            if (response.success) {
                toast.success('Documento enviado para assinatura!')
                loadDocument()
            } else {
                toast.error(
                    response.error || 'Erro ao enviar para assinatura',
                )
            }
        } catch {
            toast.error('Erro ao enviar para assinatura')
        } finally {
            setSending(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando documento...
                </p>
            </div>
        )
    }

    if (!document) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <p className="text-error">Documento não encontrado</p>
                <button
                    className="btn btn-secondary"
                    onClick={() => navigate('/commerce/subscriptions')}
                >
                    Voltar
                </button>
            </div>
        )
    }

    const statusInfo = STATUS_LABELS[document.status] || {
        label: document.status,
        className: 'badge-ghost',
    }
    const signedCount =
        document.signers?.filter(s => s.status === 'SIGNED').length || 0
    const totalSigners = document.signers?.length || 0
    const isPendingAndEditable = document.status === 'PENDING' && canUpdate

    return (
        <div className="flex flex-col gap-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/commerce/subscriptions')}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-lg" />
                </button>
                <div className="flex-1">
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                            Documento #{document.id}
                        </h1>
                        <span
                            className={`badge ${statusInfo.className}`}
                        >
                            {statusInfo.label}
                        </span>
                    </div>
                    <p className="text-sm text-base-content/60 mt-1">
                        {document.description || 'Documento de assinatura'}
                    </p>
                </div>
            </div>

            {/* Informacoes */}
            <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6">
                <h2 className="text-lg font-semibold text-base-content mb-4">
                    Informações
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <span className="text-sm text-base-content/50">
                            Origem
                        </span>
                        <p className="font-medium">
                            {getOriginLabel(document.origin)}
                            {document.originId && (
                                <span className="text-base-content/50 ml-1">
                                    #{document.originId}
                                </span>
                            )}
                        </p>
                    </div>
                    <div>
                        <span className="text-sm text-base-content/50">
                            Data de Criação
                        </span>
                        <p className="font-medium">
                            {format(
                                new Date(document.createdAt),
                                "dd/MM/yyyy 'às' HH:mm",
                                { locale: ptBR },
                            )}
                        </p>
                    </div>
                    {totalSigners > 0 && (
                        <div>
                            <span className="text-sm text-base-content/50">
                                Signatários
                            </span>
                            <p className="font-medium">
                                {signedCount} de {totalSigners} assinaram
                            </p>
                        </div>
                    )}
                    {document.envelopeId && (
                        <div>
                            <span className="text-sm text-base-content/50">
                                Envelope ID
                            </span>
                            <p className="font-mono text-sm">
                                {document.envelopeId}
                            </p>
                        </div>
                    )}
                </div>

                {/* Downloads */}
                <div className="flex gap-3 mt-6">
                    {document.documentUrl && (
                        <a
                            href={document.documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline btn-sm gap-1"
                        >
                            <MdDownload className="text-lg" />
                            PDF Original
                        </a>
                    )}
                    {document.signedDocumentUrl && (
                        <a
                            href={document.signedDocumentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-secondary btn-sm gap-1"
                        >
                            <MdDownload className="text-lg" />
                            PDF Assinado
                        </a>
                    )}
                </div>
            </div>

            {/* Signatarios - Modo interativo para PENDING */}
            {isPendingAndEditable ? (
                <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6">
                    <div className="flex gap-2 mb-4 flex-col justify-start items-start">
                        <div>
                            <h2 className="text-lg font-semibold text-base-content">
                                Signatários
                            </h2>
                            <p className="text-sm text-base-content/60">
                                Adicione os signatários e envie para assinatura
                                eletrônica
                            </p>
                        </div>
                        <button
                            type="button"
                            className="btn btn-secondary/50 btn-sm gap-1"
                            onClick={addMyself}
                            disabled={adding}
                        >
                            <MdPersonAdd className="text-lg" />
                            Me adicionar ({user?.name})
                        </button>
                    </div>

                    {/* Formulario de signatario */}
                    <div className="bg-base-200/50 rounded-xl p-4 mb-4">
                        {/* Tipo do signatario */}
                        <div className="mb-3">
                            <span className="label-text font-medium text-sm">
                                Tipo de Signatário
                            </span>
                            <div className="flex gap-1 mt-2">
                                {([
                                    { value: 'PARTNER', label: 'Parceiro' },
                                    { value: 'CLIENT', label: 'Cliente' },
                                    { value: 'OTHER', label: 'Outro' },
                                ] as const).map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        className={`btn btn-sm ${signerType === opt.value
                                            ? 'btn-secondary'
                                            : 'btn-ghost border border-base-300'
                                            }`}
                                        onClick={() => {
                                            setSignerType(opt.value)
                                            resetSignerForm()
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Campos condicionais */}
                        {signerType === 'PARTNER' && (
                            <div>
                                <PartnerSelector
                                    value={selectedPartnerId}
                                    onChange={(partnerId, partner) => {
                                        setSelectedPartnerId(partnerId)
                                        setSelectedPartner(partner)
                                        if (partner) {
                                            if (!partner.email) {
                                                toast.error('O parceiro selecionado não possui e-mail cadastrado')
                                                return
                                            }
                                            saveSignerToBackend({
                                                name: partner.name || partner.tradingName || '',
                                                email: partner.email,
                                                partnerId: partner.id,
                                                type: 'PARTNER',
                                            })
                                        }
                                    }}
                                    label="Buscar e selecionar parceiro"
                                    placeholder="Buscar parceiro..."
                                    disabled={adding}
                                />
                                {adding && signerType === 'PARTNER' && (
                                    <p className="text-xs text-base-content/60 mt-1 flex items-center gap-1">
                                        <span className="loading loading-spinner loading-xs"></span>
                                        Adicionando...
                                    </p>
                                )}
                            </div>
                        )}

                        {signerType === 'CLIENT' && (
                            <div>
                                <ClientSelector
                                    value={selectedClientId}
                                    onChange={(clientId, client) => {
                                        setSelectedClientId(clientId)
                                        setSelectedClient(client)
                                        if (client) {
                                            if (!client.email) {
                                                toast.error('O cliente selecionado não possui e-mail cadastrado')
                                                return
                                            }
                                            saveSignerToBackend({
                                                name: client.name,
                                                email: client.email,
                                                clientId: client.id,
                                                type: 'CLIENT',
                                            })
                                        }
                                    }}
                                    label="Buscar e selecionar cliente"
                                    placeholder="Buscar cliente..."
                                    disabled={adding}
                                />
                                {adding && signerType === 'CLIENT' && (
                                    <p className="text-xs text-base-content/60 mt-1 flex items-center gap-1">
                                        <span className="loading loading-spinner loading-xs"></span>
                                        Adicionando...
                                    </p>
                                )}
                            </div>
                        )}

                        {signerType === 'OTHER' && (
                            <div className="flex flex-col sm:flex-row gap-3 items-end">
                                <div className="form-control ">
                                    <label className="label">
                                        <span className="label-text font-medium text-sm">
                                            Nome
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm"
                                        placeholder="Nome completo"
                                        value={signerName}
                                        onChange={e =>
                                            setSignerName(e.target.value)
                                        }
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault()
                                                addSigner()
                                            }
                                        }}
                                    />
                                </div>
                                <div className="form-control ">
                                    <label className="label">
                                        <span className="label-text font-medium text-sm">
                                            E-mail
                                        </span>
                                    </label>
                                    <input
                                        type="email"
                                        className="input input-bordered input-sm"
                                        placeholder="email@exemplo.com"
                                        value={signerEmail}
                                        onChange={e =>
                                            setSignerEmail(e.target.value)
                                        }
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault()
                                                addSigner()
                                            }
                                        }}
                                    />
                                </div>
                                <button
                                    className="btn btn-outline btn-sm gap-1 flex-shrink-0"
                                    onClick={addSigner}
                                    disabled={adding}
                                >
                                    {adding ? (
                                        <span className="loading loading-spinner loading-xs"></span>
                                    ) : (
                                        <MdAdd className="text-lg" />
                                    )}
                                    Adicionar
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Lista de signatarios salvos */}
                    {document.signers?.length > 0 ? (
                        <div className="space-y-2 mb-6">
                            {document.signers.map(signer => (
                                <div
                                    key={signer.id}
                                    className="flex items-center justify-between p-3 rounded-lg bg-base-200/30 border border-base-300/30"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                                            <MdPerson className="text-lg text-secondary" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-sm">
                                                {signer.name}
                                            </p>
                                            <p className="text-xs text-base-content/60">
                                                {signer.email} -{' '}
                                                {SIGNER_TYPE_LABELS[signer.type] || 'Outro'}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-ghost btn-xs text-error"
                                        onClick={() => removeSigner(signer.id)}
                                    >
                                        <MdDelete className="text-base" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-center text-base-content/50 text-sm py-4 mb-4">
                            Nenhum signatário adicionado
                        </p>
                    )}

                    {/* Botao enviar */}
                    <div className="flex justify-end">
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={handleSendForSigning}
                            disabled={sending || !document.signers?.length}
                        >
                            {sending ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                <MdSend className="text-lg" />
                            )}
                            Enviar para Assinatura
                        </button>
                    </div>
                </div>
            ) : (
                /* Signatarios - Modo somente leitura */
                <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Signatários
                    </h2>
                    {document.signers?.length === 0 ? (
                        <p className="text-base-content/60 text-sm">
                            Nenhum signatário cadastrado
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {document.signers?.map(signer => (
                                <div
                                    key={signer.id}
                                    className="flex items-center gap-4 p-4 rounded-xl bg-base-200/50 border border-base-300/30"
                                >
                                    <div className="w-10 h-10 rounded-full bg-base-300 flex items-center justify-center flex-shrink-0">
                                        <MdPerson className="text-xl text-base-content/60" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-base-content truncate">
                                            {signer.name}
                                        </p>
                                        <p className="text-sm text-base-content/60 truncate">
                                            {signer.email}
                                        </p>
                                        {(signer.partner ||
                                            signer.client) && (
                                                <p className="text-xs text-base-content/40 mt-0.5">
                                                    {signer.type === 'PARTNER'
                                                        ? signer.partner
                                                            ?.tradingName ||
                                                        signer.partner?.name
                                                        : signer.client?.name}
                                                </p>
                                            )}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <span className="badge badge-sm badge-outline">
                                            {SIGNER_TYPE_LABELS[signer.type] || 'Outro'}
                                        </span>
                                        {signer.status === 'SIGNED' ? (
                                            <span className="badge badge-sm badge-success gap-1">
                                                <MdCheckCircle className="text-xs" />
                                                Assinado
                                            </span>
                                        ) : (
                                            <span className="badge badge-sm badge-warning gap-1">
                                                <MdHourglassEmpty className="text-xs" />
                                                Aguardando
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
